import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { apiErr } from "@/lib/api";
import api from "@/lib/api";
import { BvdLogo, Fhi360Logo } from "@/components/Logo";
import { KeyRound } from "lucide-react";
import { ShieldCheck, Lock, Mail, ArrowLeft, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login, verifyLoginCode, resendLoginCode } = useAuth();
  const { t, lang, setLang } = useI18n();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  // Étape de confirmation par e-mail
  const [step, setStep] = useState("creds"); // creds | code
  const [challenge, setChallenge] = useState("");
  const [backupAllowed, setBackupAllowed] = useState(false);
  const [useBackup, setUseBackup] = useState(false);
  const [emailHint, setEmailHint] = useState("");
  const [code, setCode] = useState("");
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const id = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await login(email, password, remember);
      if (data?.mfa_required) {
        setChallenge(data.challenge);
        setEmailHint(data.email_hint || "");
        setBackupAllowed(!!data.backup_allowed);
        setUseBackup(!!data.email_failed);
        setStep("code");
        setCooldown(60);
        if (data.email_failed) toast.warning(t("lg_email_down"));
        else toast.message(`${t("lg_code_sent")} ${data.email_hint || t("lg_your_email")}`);
      } else {
        nav("/app");
      }
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await verifyLoginCode(challenge, code);
      nav("/app");
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    if (cooldown > 0) return;
    try {
      const data = await resendLoginCode(challenge);
      setCooldown(60);
      setCode("");
      toast.success(`${t("lg_new_code_sent")} ${data.email_hint || t("lg_your_email")}`);
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail) || err.message);
    }
  };

  const backToCreds = () => { setStep("creds"); setCode(""); setChallenge(""); setUseBackup(false); };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr] bg-slate-50">
      {/* left brand panel */}
      <div className="hidden lg:flex flex-col justify-between p-12 xl:p-16 relative overflow-hidden bg-slate-950">
        <div className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, #ffffff 1px, transparent 0)", backgroundSize: "28px 28px" }} />
        <div className="flex items-center gap-3 relative">
          <BvdLogo className="w-10 h-10 text-white" />
          <div>
            <div className="font-head font-bold text-xl text-white tracking-tight">BVD-Ituri</div>
            <div className="text-[11px] font-mono uppercase tracking-[0.14em] text-slate-400">{t("lg_bvd")}</div>
          </div>
        </div>
        <div className="relative">
          <h2 className="font-head font-bold text-4xl xl:text-[2.75rem] text-white leading-[1.1] tracking-tight">
            {t("lg_hero_1")}<br />{t("lg_hero_2")}
          </h2>
          <p className="text-slate-400 mt-5 max-w-md text-sm leading-relaxed">
            {t("lg_hero_desc")}
          </p>
          <div className="flex items-center gap-2 mt-7 text-xs font-mono text-emerald-300">
            <ShieldCheck className="w-4 h-4" /> {t("lg_security_line")}
          </div>
        </div>
        <div className="relative flex items-center gap-3 text-xs font-mono text-slate-500">
          <Fhi360Logo className="h-9 w-auto" dark />
          <span className="w-px h-3 bg-white/20" />
          <span>STRIDES · EpiC-GHS · EpiC-Crisis</span>
        </div>
      </div>

      {/* right form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm animate-fade-up">
          <div className="flex justify-between items-center mb-8">
            <div className="lg:hidden"><BvdLogo className="w-8 h-8 text-slate-900" /></div>
            <div className="flex items-center gap-0.5 border border-slate-200 rounded-md p-0.5 ml-auto">
              {["fr", "en"].map((l) => (
                <button key={l} data-testid={`language-toggle-${l}`} onClick={() => setLang(l)}
                  className={`px-2.5 py-1 rounded text-xs font-mono font-semibold uppercase ${lang === l ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900"}`}>
                  {l}
                </button>
              ))}
            </div>
          </div>

          {step === "creds" ? (
            <>
              <h1 className="font-head font-bold text-2xl text-slate-900 tracking-tight">{t("login_title")}</h1>
              <p className="text-sm text-slate-500 mt-1.5 mb-7">{t("login_desc")}</p>

              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label className="text-xs font-medium text-slate-600">{t("email_or_id")}</label>
                  <input data-testid="login-email-input" type="text" required value={email} onChange={(e) => setEmail(e.target.value)}
                    className="mt-1.5 w-full bg-[#FFFFFF] border border-slate-200 rounded-md px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/40 transition-all" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600">{t("password")}</label>
                  <div className="relative mt-1.5">
                    <input data-testid="login-password-input" type={showPw ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)}
                      className="w-full bg-[#FFFFFF] border border-slate-200 rounded-md px-3 py-2.5 pr-10 text-sm text-slate-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/40 transition-all" />
                    <button type="button" data-testid="toggle-password-visibility" onClick={() => setShowPw((v) => !v)}
                      className="absolute inset-y-0 right-0 flex items-center px-3 text-slate-400 hover:text-slate-700 transition-colors" aria-label={showPw ? "Masquer le mot de passe" : "Afficher le mot de passe"}>
                      {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <label className="flex items-center gap-2 text-sm text-slate-600 select-none cursor-pointer">
                  <input data-testid="remember-me" type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)}
                    className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500/40" />
                  {t("remember_me")}
                </label>
                <button data-testid="login-submit-button" type="submit" disabled={loading}
                  className="w-full flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 disabled:opacity-60 text-white font-semibold py-2.5 rounded-md transition-colors">
                  <Lock className="w-4 h-4" /> {loading ? t("signing") : t("signin")}
                </button>
              </form>

              <div className="mt-8 pt-6 border-t border-slate-200 flex flex-col gap-1.5 text-sm">
                <span className="text-slate-500">{t("login_no_account")}</span>
                <Link to="/activer" data-testid="login-activer-link" className="flex items-center gap-1.5 font-semibold text-slate-900 hover:underline"><KeyRound className="w-3.5 h-3.5" /> {t("login_activate")}</Link>
              </div>
            </>
          ) : (
            <div data-testid="login-code-step">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-blue-50 text-blue-700 mb-4"><Mail className="w-6 h-6" /></div>
              <h1 className="font-head font-bold text-2xl text-slate-900 tracking-tight">{useBackup ? t("lg_backup_code") : t("lg_email_confirm")}</h1>
              <p className="text-sm text-slate-500 mt-1.5 mb-6">
                {useBackup ? t("lg_backup_hint") : <>{t("lg_code_sent_6")} <span className="font-semibold text-slate-700">{emailHint}</span> {t("lg_enter_below")}</>}
              </p>
              <form onSubmit={verify} className="space-y-4">
                <div>
                  <label className="text-xs font-medium text-slate-600">{useBackup ? t("lg_backup_code") : t("lg_login_code")}</label>
                  {useBackup ? (
                    <input data-testid="login-backup-input" autoFocus value={code}
                      onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, "").slice(0, 9))}
                      className="mt-1.5 w-full bg-[#FFFFFF] border border-slate-200 rounded-md px-3 py-2.5 text-center text-lg text-slate-900 outline-none focus:border-blue-500 tracking-[0.3em] font-mono" placeholder="XXXX-XXXX" />
                  ) : (
                  <input data-testid="login-code-input" inputMode="numeric" autoFocus value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    className="mt-1.5 w-full bg-[#FFFFFF] border border-slate-200 rounded-md px-3 py-2.5 text-center text-lg text-slate-900 outline-none focus:border-blue-500 tracking-[0.5em] font-mono" placeholder="000000" />
                  )}
                </div>
                <button data-testid="login-code-submit" type="submit" disabled={loading || (useBackup ? code.replace(/-/g, "").length !== 8 : code.length !== 6)}
                  className="w-full flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 disabled:opacity-60 text-white font-semibold py-2.5 rounded-md transition-colors">
                  <ShieldCheck className="w-4 h-4" /> {loading ? t("lg_verifying") : t("lg_validate_login")}
                </button>
              </form>
              {backupAllowed && (
                <button data-testid="login-toggle-backup" onClick={() => { setUseBackup(!useBackup); setCode(""); }} className="mt-3 text-xs text-amber-700 hover:text-amber-600 font-semibold">
                  {useBackup ? t("lg_use_email_code") : t("lg_use_backup")}
                </button>
              )}
              <div className="flex items-center justify-between mt-5 text-sm">
                <button data-testid="login-code-back" onClick={backToCreds} className="flex items-center gap-1.5 text-slate-500 hover:text-slate-800 font-semibold">
                  <ArrowLeft className="w-3.5 h-3.5" /> {t("back")}
                </button>
                <button data-testid="login-code-resend" onClick={resend} disabled={cooldown > 0}
                  className="text-blue-700 hover:text-blue-600 disabled:text-slate-400 font-semibold">
                  {cooldown > 0 ? `${t("lg_resend")} (${cooldown}s)` : t("lg_resend")}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
